// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode, WNodeFactory, WidgetProperties } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Button } from '@dojo/widgets/button';
import { Icon } from '@holmescorp/base-widgets/icon';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, lccstate, pathmap, rollupStatus, userstate } from '@holmescorp/lcc-state';
import { ParentTree, TopicAttrs } from '@holmescorp/lcc-state/interfaces';
import { logUser } from '@holmescorp/logger';

import css from '../css/topicgroup.m.css';
import { TopicProperties } from '../interfaces';

export interface TopicRefProperties extends TopicProperties {
	showShort?: boolean;
	action?: () => void;
}

interface TopicRefIcache {}

const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const icache = createICacheMiddleware<TopicRefIcache>();
const factory = create({ icache, theme, nls, settings, lccstate, userstate }).properties<TopicRefProperties>();

export const TopicRef = factory(({ properties, middleware: { theme, nls, settings, userstate } }) => {
	const status: ParentTree = userstate.get(userstate.path('status'));
	const {
		topic: { attributes = {} as TopicAttrs },
		action,
		showShort = false
	} = properties();
	const { key, title, rel = '' } = attributes;
	const { complete } = status[rel].data;
	const parentRel = status[rel].parent ? `${status[rel].parent}` : '';
	const {
		messages: { read: readText = '' /* completed = '', incompleted = '' */ }
	} = nls();
	const themeCss = theme.classes(css);

	const { messages: settingsMessages } = settings();
	const { is_demo = false, demo_allowed_topics = '[]' } = settingsMessages;
	let allowed_topics: string[] = [];
	try {
		allowed_topics = JSON.parse(demo_allowed_topics);
	} catch (e) {
		console.warn(`json parse error ${e}`, demo_allowed_topics);
	}
	const isLocked = is_demo && allowed_topics.indexOf(parentRel) === -1 && allowed_topics.indexOf(rel) === -1;
	const lockIcon = isLocked ? v('span', { classes: [themeCss.lockIcon] }, [w(Icon, { faname: 'lock' })]) : null;

	const render_fragment = (): DNode => {
		const { topic } = properties();
		return w('Generator', { skipTitle: true, topic });
	};

	const mark_read = (rel: string) => {
		const dflt: any = {};
		dflt[rel] = { data: {} };
		const _status = status || (dflt as ParentTree);
		const data = { ..._status[rel].data };
		const value = data.complete === 1 ? 0 : 1;
		data.complete = value;
		const { path } = keymap.get(rel)!;

		logUser.info(`${path} flagged as ${value === 0 ? 'unread' : 'read'}`);
		rollupStatus(rel, data);
	};

	return v('div', { key, xqa: `topicref ${rel}` }, [
		v('div', { classes: [themeCss.refTitleBlock] }, [
			complete === 1
				? v(
						'div',
						{
							classes: [themeCss.complete],
							tabIndex: 0,
							role: 'checkbox',
							'aria-checked': 'true',
							'aria-labelledby': `${rel}-title`,
							onclick: (event) => {
								event && event.stopPropagation();
								mark_read(rel);
							},
							onkeydown: (event: KeyboardEvent) => {
								switch (event.key) {
									case Keys.Enter:
									case Keys.Space:
									case Keys.Spacebar:
										event.preventDefault();
										mark_read(rel);
								}
							}
						},
						[v('i', { 'aria-hidden': 'true', classes: ['fa', 'fa-lg', 'fa-check-square'] })]
				  )
				: v(
						'div',
						{
							classes: [themeCss.incomplete],
							tabIndex: 0,
							role: 'checkbox',
							'aria-checked': 'false',
							'aria-labelledby': `${rel}-title`,
							onclick: (event) => {
								event && event.stopPropagation();
								if (isLocked) {
									return;
								}
								mark_read(rel);
							},
							onkeydown: (event: KeyboardEvent) => {
								event && event.stopPropagation();
								switch (event.key) {
									case Keys.Enter:
									case Keys.Space:
									case Keys.Spacebar:
										event.preventDefault();
										if (isLocked) {
											return;
										}
										mark_read(rel);
								}
							}
						},
						[v('i', { 'aria-hidden': 'true', classes: ['far', 'fa-lg', 'fa-square'] })]
				  ),
			v('div', { id: `${rel}-title`, classes: [themeCss.refTitle] }, [
				showShort
					? v(
							'span',
							{
								tabIndex: 0,
								classes: [themeCss.refTitleAction],
								onclick: (event) => {
									event && event.stopPropagation();
									if (isLocked) {
										return;
									}
									const { path } = keymap.get(rel)!;
									const { view } = pathmap.get(path)!;
									action && action();
									// publishSync('use-main-view');
									view();
								},
								onkeydown: (event: KeyboardEvent) => {
									const { path } = keymap.get(rel)!;
									const { view } = pathmap.get(path)!;

									switch (event.key) {
										case Keys.Enter:
										case Keys.Space:
										case Keys.Spacebar:
											event && event.stopPropagation();
											if (isLocked) {
												return;
											}
											action && action();
											// publishSync('use-main-view');
											view();
											break;
									}
								}
							},
							[title]
					  )
					: title
			])
		]),
		showShort ? null : rel && render_fragment(),
		showShort
			? null
			: w(
					Button,
					{
						onClick: () => {
							if (isLocked) {
								return;
							}
							const { path } = keymap.get(rel)!;
							const { view } = pathmap.get(path)!;
							action && action();
							// publishSync('use-main-view');
							view();
						},
						onDown: () => {
							if (isLocked) {
								return;
							}
							const { path } = keymap.get(rel)!;
							const { view } = pathmap.get(path)!;
							action && action();
							// publishSync('use-main-view');
							view();
						},
						aria: { label: `${readText} ${title}` }
					},
					[
						{
							icon: lockIcon,
							label: readText
						}
					]
			  )
	]);
});
